import { Memoize } from "typescript-memoize";
import { AnalyticsManager } from "./analytics/analyticsManager";
import { Api } from "./api/api";

import { InvoiceApiClient } from "./api/invoice.api.client";
import { PaymentApiClient } from "./api/payment.api.client";
import { ProvidersApiClient } from "./api/providers.api.client";
import { ConfirmationStore } from "./sections/confirmation/confirmationStore";
import { InvoiceLinkStore } from "./sections/invoiceLink/invoiceLinkStore";
import { ObPaymentPageFormStore } from "./sections/obPaymentPage/obPaymentPageFormStore";
import { ObPaymentPageViewStore } from "./sections/obPaymentPage/obPaymentPageView/obPaymentPageViewStore";
import { BanksPageStore } from "./sections/payment/banks/banksPageStore";
import { CardPageStore } from "./sections/payment/card/cardPageStore";
import { PaymentStore } from "./sections/payment/paymentStore";
import { PaymentLinkFormStore } from "./sections/paymentLink/components/form/formStore";
import { PaymentLinkStore } from "./sections/paymentLink/paymentLinkStore";

export type Config = {
  publicApiUrl: string;
  stripeApiKey?: string;
  googleMapsApiKey?: string;
  authUrl: string;
  clientId: string;
  adminUrl: string;
  webticsTrackerUrl: string;
};

export class Services {
  constructor(config: Config) {
    this._config = config;
  }

  private readonly _config: Config;

  get config(): Readonly<Config> {
    return this._config;
  }

  @Memoize()
  get apiClient(): Api {
    return new Api(this._config.publicApiUrl);
  }

  @Memoize()
  get annaAnalyticsManager(): AnalyticsManager {
    return new AnalyticsManager(this._config.publicApiUrl, this._config.webticsTrackerUrl);
  }

  @Memoize()
  get invoiceClient(): InvoiceApiClient {
    return new InvoiceApiClient(this.apiClient);
  }

  @Memoize()
  get paymentClient(): PaymentApiClient {
    return new PaymentApiClient(this.apiClient);
  }

  @Memoize()
  get providerClient(): ProvidersApiClient {
    return new ProvidersApiClient(this.apiClient);
  }

  createPaymentLinkStore(): PaymentLinkStore {
    return new PaymentLinkStore(this.paymentClient);
  }

  createPaymentLinkFormStore(): PaymentLinkFormStore {
    return new PaymentLinkFormStore(this.paymentClient);
  }

  createConfirmationStore(): ConfirmationStore {
    return new ConfirmationStore(this.invoiceClient);
  }

  createCardPageStore(): CardPageStore {
    return new CardPageStore(this.config.stripeApiKey);
  }

  createBanksPageStore(): BanksPageStore {
    return new BanksPageStore(this.providerClient);
  }

  createPaymentStore(): PaymentStore {
    return new PaymentStore(this.paymentClient);
  }

  createInvoiceLinkStore(): InvoiceLinkStore {
    return new InvoiceLinkStore(this.invoiceClient);
  }

  createObPaymentPageFormStore(): ObPaymentPageFormStore {
    return new ObPaymentPageFormStore(this.paymentClient);
  }

  createObPaymentPageViewStore(): ObPaymentPageViewStore {
    return new ObPaymentPageViewStore(this.paymentClient);
  }
}
